import { Button } from '@/components/ui/button'
import { useJobillaOrgId } from '@/hooks'
import { createStore, useAtomValue } from 'jotai'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	BrandEditor,
	BrandSelector,
	getBrandValidationErrorMessage,
	useBrandLibraryStore,
	validateBrandSet,
} from '../../constructs/branding/simplified'
import { BrandLibraryPicker } from '../../constructs/branding/simplified/brand-library'
import { BrandLibraryStoreProvider } from '../../constructs/branding/simplified/brand-library-store'
import { brandLibraryAtom, brandLibraryLoadingAtom } from '../../constructs/branding/simplified/state'
import { ProvidedSettingsNavConfigContext } from '../../core/settings-config-context'
import { AnimateChangeInHeight } from '../../rapid-ui'
import { ConfirmationDialog, ConfirmationDialogRef } from '../../rapid-ui/structural'
import { logger } from '../../utilities'
import { ConformedBranding, MediaAsset } from '../../views'

export type BrandingAreaProps = {
	branding: ConformedBranding | null
	onBrandingChange: (branding: ConformedBranding | null) => void
	onLogoUpload?: (logo: File, callback: (asset: MediaAsset) => void) => void
	brandStore?: ReturnType<typeof createStore>
	enableCustomization?: boolean
	autoShowCustomizations?: boolean
	campaignId?: string
}
export function BrandingArea({
	branding,
	onBrandingChange,
	onLogoUpload,
	brandStore,
	enableCustomization = true,
	autoShowCustomizations = true,
	campaignId,
}: BrandingAreaProps) {
	const { t } = useTranslation('common')
	const store = useBrandLibraryStore({ overrideStore: brandStore })
	const { brandingLibraryLinker } = useContext(ProvidedSettingsNavConfigContext)
	const jobillaOrgId = useJobillaOrgId()

	const brandLibrary = useAtomValue(brandLibraryAtom, { store })
	const brandLibraryLoading = useAtomValue(brandLibraryLoadingAtom, { store })

	const errorMessage = getBrandValidationErrorMessage(brandLibrary, branding, brandLibraryLoading)
	const validation = validateBrandSet(branding)

	// Campaign has a branding snapshot whose source no longer exists in the library
	const brandSourceMissing = useMemo(() => {
		if (!branding?.id) { return false }
		return !brandLibrary.find((b) => b.id === branding.id)
	}, [branding, brandLibrary])

	// Check if the campaign's branding differs from the org branding set
	const hasCustomizations = useMemo(() => {
		if (!branding) { return false }

		// Find the matching org-level branding set
		const orgBranding = brandLibrary.find((b) => b.id === branding.id)
		if (!orgBranding) { return false }

		// Check if any fields differ
		if (branding.logoId !== orgBranding.logoId) { return true }
		if (branding.website !== orgBranding.website) { return true }
		if (branding.toneOfVoice !== orgBranding.toneOfVoice) { return true }
		if (branding.visualGuidance !== orgBranding.visualGuidance) { return true }
		if (branding.bodyFont !== orgBranding.bodyFont) { return true }
		if (branding.titleFont !== orgBranding.titleFont) { return true }

		// Check if color fields differ
		if (branding.foreground !== orgBranding.foreground) { return true }
		if (branding.background !== orgBranding.background) { return true }
		if (branding.accentForeground !== orgBranding.accentForeground) { return true }
		if (branding.accentBackground !== orgBranding.accentBackground) { return true }

		return false
	}, [branding, brandLibrary])

	const overrideDialogRef = useRef<ConfirmationDialogRef>(null)

	const [isCustomizing, setIsCustomizing] = useState(autoShowCustomizations && hasCustomizations)
	const [scanUrl, setScanUrl] = useState('')
	const [brandLibraryOpen, setBrandLibraryOpen] = useState(false)

	// Update isCustomizing when hasCustomizations changes (only if autoShowCustomizations is enabled)
	// Also auto-open when the source brand was deleted so the user can see the campaign's branding
	useEffect(() => {
		if (autoShowCustomizations) {
			setIsCustomizing(hasCustomizations || brandSourceMissing)
		}
	}, [hasCustomizations, brandSourceMissing, autoShowCustomizations])

	if (brandLibraryLoading) {
		return (
			<div className="pt-2 flex flex-col gap-2">
				<div className="text-lg font-medium">{t('branding.brandSettings')}</div>
				<div className="flex items-center gap-2 text-sm text-grey-600">
					<div className="animate-spin h-4 w-4 border-2 border-grey-300 border-t-grey-600 rounded-full" />
					<span>{t('branding.loadingBrandSets')}</span>
				</div>
			</div>
		)
	}

	if (brandLibrary.length === 0 && !branding) {
		return (
			<div className="pt-2 flex flex-col gap-3">
				<div className="text-lg font-medium">{t('branding.brandSettings')}</div>
				<div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
					<div className="font-medium text-red-900 mb-1">{t('branding.noBrandSetsAvailable')}</div>
					<div className="text-red-700">
						{t('branding.noBrandSetsCreatePrompt')}{' '}
						<Button
							variant="link"
							onClick={() => setBrandLibraryOpen(true)}
							className="p-0 h-auto font-medium text-red-700 hover:text-red-900"
						>
							{t('branding.openBrandingLibrary')}
						</Button>
					</div>
				</div>
				<BrandLibraryStoreProvider store={store}>
					<BrandLibraryPicker
						open={brandLibraryOpen}
						onSelect={(selectedBranding) => {
							onBrandingChange(selectedBranding)
							setBrandLibraryOpen(false)
						}}
						onClose={() => setBrandLibraryOpen(false)}
						campaignId={campaignId}
					/>
				</BrandLibraryStoreProvider>
			</div>
		)
	}

	const handleBrandSelect = async (value: string) => {
		if (brandSourceMissing || hasCustomizations) {
			try {
				await overrideDialogRef.current?.confirm({
					title: t('branding.overrideBrandingTitle'),
					message: t('branding.overrideBrandingMessage'),
					confirmText: t('branding.overrideBrandingConfirm'),
					cancelText: t('common.cancel'),
				})
			} catch {
				return
			}
		}

		const selectedBrand = brandLibrary.find((b) => b.id === value) ?? null
		onBrandingChange(selectedBrand)
		setIsCustomizing(false)
	}

	const handleBrandingEdit = (updatedBranding: ConformedBranding) => {
		// When editing, directly update the campaign's branding
		onBrandingChange(updatedBranding)
	}

	const handleCustomizeClick = () => {
		const willBeOpen = !isCustomizing

		// Track when user toggles branding customization
		if (campaignId) {
			logger.track('Editor: Toggled branding customization', {
				data: {
					campaign_id: campaignId,
					organisation_id: jobillaOrgId,
					company: jobillaOrgId,
					is_toggled_open: willBeOpen,
				},
			})
		}
		setIsCustomizing(willBeOpen)
	}

	return (
		<div className="pt-2 flex flex-col gap-2">
			<div className="flex items-center justify-between">
				<div className="text-lg font-medium">
					{t('branding.brandSettings')}
				</div>

				{enableCustomization && (
					<button
						onClick={handleCustomizeClick}
						className="text-sm text-grey-500 hover:text-grey-900 text-center underline"
					>
						{isCustomizing ? t('branding.close') : t('branding.customize')}
					</button>
				)}
			</div>
			{brandLibrary.length > 0 && (
				<BrandSelector
					items={brandLibrary}
					value={branding?.id ?? ''}
					onChange={handleBrandSelect}
					brandStore={store}
					campaignId={campaignId}
				/>
			)}
			{brandSourceMissing && !brandLibraryLoading && (
				<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
					<div className="text-amber-700">
						{t('branding.sourceBrandDeleted')}{' '}
						<Button
							variant="link"
							onClick={() => setBrandLibraryOpen(true)}
							className="p-0 h-auto font-medium text-amber-700 hover:text-amber-900"
						>
							{t('branding.openBrandingLibrary')}
						</Button>
					</div>
				</div>
			)}
			{errorMessage && !brandLibraryLoading && (
				<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
					<div className="font-medium text-amber-900 mb-1">{t('branding.brandValidationIssue')}</div>
					<div className="text-amber-700">{errorMessage}</div>
				</div>
			)}
			{branding && !validation.isValid && (
				<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
					<div className="font-medium text-amber-900 mb-1">{t('branding.incompleteBrandSet')}</div>
					<ul className="text-amber-700 list-disc list-inside">
						{validation.errors.map((error, i) => <li key={i}>{error}</li>)}
					</ul>
				</div>
			)}
			<AnimateChangeInHeight>
				{isCustomizing && branding && (
					<div className="bg-grey-50 rounded-lg overflow-hidden relative">
						<BrandEditor
							branding={branding}
							onChange={handleBrandingEdit}
							urlValue={scanUrl}
							onUrlChange={setScanUrl}
							onLogoUpload={onLogoUpload}
							enableScanning={false}
						/>
					</div>
				)}
			</AnimateChangeInHeight>
			<ConfirmationDialog ref={overrideDialogRef} />
			<BrandLibraryStoreProvider store={store}>
				<BrandLibraryPicker
					open={brandLibraryOpen}
					onSelect={(selectedBranding) => {
						handleBrandSelect(selectedBranding.id)
						setBrandLibraryOpen(false)
					}}
					onClose={() => setBrandLibraryOpen(false)}
					campaignId={campaignId}
				/>
			</BrandLibraryStoreProvider>
		</div>
	)
}
